import { useCallback, useState } from "react";
import { useAppSelector } from "@/app/hooks";
import { selectUser } from "@/features/auth/authSlice";
import { softRefreshNinjaIbvReport } from "@/lib/api/bankService";
import { showApiError, showSuccess } from "@/lib/utils/notifications";
import { shouldShowResubmitIncomeReportButton } from "../../../../utils/creditReportHelpers";

export function useResubmitIncomeReport(options: {
  applicantId: string;
  applicationStatus?: string | null;
  hasBankLinkingSteps?: boolean;
  onRefresh?: () => void | Promise<void>;
  refreshAccounts?: () => void | Promise<void>;
}) {
  const {
    applicantId,
    applicationStatus,
    hasBankLinkingSteps = false,
    onRefresh,
    refreshAccounts,
  } = options;

  const user = useAppSelector(selectUser);
  const currentUserRole = user?.current_role?.role_name || "";

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [processing, setProcessing] = useState(false);

  const showResubmitIncomeReport = shouldShowResubmitIncomeReportButton({
    currentUserRole,
    applicationStatus,
    hasBankLinkingSteps,
  });

  const openConfirm = useCallback(() => {
    setConfirmOpen(true);
  }, []);

  const closeConfirm = useCallback(() => {
    setConfirmOpen(false);
  }, []);

  const performResubmit = useCallback(async () => {
    setConfirmOpen(false);
    if (!applicantId?.trim() || processing) return;
    setProcessing(true);
    try {
      const response = await softRefreshNinjaIbvReport(applicantId);
      const msg =
        typeof response?.message === "string" && response.message.trim()
          ? response.message
          : "Income report has been retried.";
      showSuccess(msg);
      if (refreshAccounts) await refreshAccounts();
      if (onRefresh) await onRefresh();
    } catch (error: unknown) {
      showApiError(error);
    } finally {
      setProcessing(false);
    }
  }, [applicantId, onRefresh, processing, refreshAccounts]);

  return {
    showResubmitIncomeReport,
    processing,
    confirmOpen,
    openConfirm,
    closeConfirm,
    performResubmit,
  };
}
